import React, { useState } from 'react';
import { Mail, Phone, MapPin, CheckCircle2 } from 'lucide-react';
import { Reveal, SectionLabel, BtnRed } from '../components/common';
import { images, spaceOptions, contactSteps, contactInfo } from '../mock/mock';
import { useToast } from '../hooks/use-toast';
import { Toaster } from '../components/ui/toaster';

const initialForm = {
  nombre: '', empresa: '', email: '', telefono: '', tipoEspacio: '',
  ciudad: '', objetivo: '', fecha: '', presupuesto: '', mensaje: '', acepta: false,
};

export default function Hablemos() {
  const [form, setForm] = useState(initialForm);
  const [sent, setSent] = useState(false);
  const { toast } = useToast();

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email || !form.acepta) {
      toast({ title: 'Faltan datos', description: 'Completa nombre, email y acepta la política de privacidad.' });
      return;
    }
    // Save locally
    const stored = JSON.parse(localStorage.getItem('mt_leads') || '[]');
    stored.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem('mt_leads', JSON.stringify(stored));

    // Send to server via PHP
    fetch('/send.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(form),
    }).catch(() => {});

    setSent(true);
    toast({ title: 'Propuesta enviada', description: 'Gracias, te contactaremos muy pronto.' });
  };

  const inputCls =
    'w-full bg-[var(--mt-bg-card)] border border-white/10 focus:border-[var(--mt-red)] text-white placeholder-white/35 px-4 py-3 outline-none transition-colors';

  return (
    <div>
      <Toaster />
      {/* HERO */}
      <section className="relative min-h-[60vh] flex items-center overflow-hidden">
        <img src={images.stageRed} alt="" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#0b0b0d]/85 via-[#0b0b0d]/70 to-[#0b0b0d]" />
        <div className="relative max-w-[1280px] mx-auto px-5 md:px-8 pt-28 w-full">
          <Reveal><SectionLabel light>Todo empieza con una conversación</SectionLabel></Reveal>
          <Reveal delay={100}>
            <h1 className="font-display font-bold uppercase text-4xl md:text-6xl leading-[1.02] text-white max-w-3xl">
              Cuéntanos qué <span className="text-[var(--mt-red)]">quieres activar.</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="mt-7 max-w-xl text-white/70 leading-relaxed">
              Puede ser un espacio, una idea, una necesidad o un proyecto todavía sin forma. No hace falta llegar con
              todas las respuestas. Empezamos escuchando.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FORM + INFO */}
      <section className="py-24 md:py-28">
        <div className="max-w-[1280px] mx-auto px-5 md:px-8 grid lg:grid-cols-3 gap-12">
          {/* Form */}
          <div className="lg:col-span-2">
            <Reveal>
              <SectionLabel>Cuéntanos por dónde empezamos</SectionLabel>
              <h2 className="font-display font-bold uppercase text-2xl md:text-4xl text-white leading-tight mb-8">
                Antes de proponer, queremos entender.
              </h2>
            </Reveal>

            {sent ? (
              <Reveal>
                <div className="bg-[var(--mt-bg-card)] border border-[var(--mt-red)]/40 p-10 text-center">
                  <CheckCircle2 size={48} className="text-[var(--mt-red)] mx-auto" />
                  <h3 className="font-display font-bold uppercase text-2xl text-white mt-5">¡Gracias!</h3>
                  <p className="text-white/60 mt-3 max-w-md mx-auto">
                    Hemos recibido tu propuesta. Revisaremos la información y te contactaremos para entender mejor el proyecto.
                  </p>
                  <button
                    onClick={() => { setForm(initialForm); setSent(false); }}
                    className="mt-6 font-display uppercase tracking-wider text-sm text-[var(--mt-red)] hover:text-white transition-colors"
                  >
                    Enviar otra propuesta
                  </button>
                </div>
              </Reveal>
            ) : (
              <Reveal>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <input className={inputCls} placeholder="Nombre *" value={form.nombre} onChange={(e) => update('nombre', e.target.value)} />
                    <input className={inputCls} placeholder="Empresa u organización" value={form.empresa} onChange={(e) => update('empresa', e.target.value)} />
                    <input className={inputCls} type="email" placeholder="Correo electrónico *" value={form.email} onChange={(e) => update('email', e.target.value)} />
                    <input className={inputCls} placeholder="Teléfono" value={form.telefono} onChange={(e) => update('telefono', e.target.value)} />
                    <select className={`${inputCls} appearance-none`} value={form.tipoEspacio} onChange={(e) => update('tipoEspacio', e.target.value)}>
                      <option value="">Tipo de espacio</option>
                      {spaceOptions.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                    <input className={inputCls} placeholder="Ciudad" value={form.ciudad} onChange={(e) => update('ciudad', e.target.value)} />
                    <input className={inputCls} placeholder="Fecha aproximada" value={form.fecha} onChange={(e) => update('fecha', e.target.value)} />
                    <input className={inputCls} placeholder="Presupuesto orientativo" value={form.presupuesto} onChange={(e) => update('presupuesto', e.target.value)} />
                  </div>
                  <input className={inputCls} placeholder="Qué te gustaría conseguir" value={form.objetivo} onChange={(e) => update('objetivo', e.target.value)} />
                  <textarea className={`${inputCls} min-h-[130px] resize-none`} placeholder="Mensaje" value={form.mensaje} onChange={(e) => update('mensaje', e.target.value)} />

                  <label className="flex items-center gap-3 text-white/60 text-sm cursor-pointer select-none">
                    <input type="checkbox" checked={form.acepta} onChange={(e) => update('acepta', e.target.checked)} className="w-4 h-4 accent-[var(--mt-red)]" />
                    He leído y acepto la política de privacidad.
                  </label>

                  <div className="pt-2">
                    <BtnRed type="submit">Enviar propuesta</BtnRed>
                  </div>
                  <p className="text-white/40 text-xs">Responderemos para entender mejor el proyecto y valorar el siguiente paso.</p>
                </form>
              </Reveal>
            )}
          </div>

          {/* Info */}
          <div>
            <Reveal delay={150}>
              <div className="bg-[var(--mt-bg-alt)] border border-white/5 p-8 sticky top-24">
                <span className="font-display uppercase tracking-[0.25em] text-xs text-[var(--mt-red)]">También puedes escribirnos</span>
                <h3 className="font-display font-bold uppercase text-xl text-white mt-3">Contacto directo</h3>
                <ul className="mt-6 space-y-4">
                  <li className="flex items-center gap-3 text-white/65 text-sm"><Mail size={18} className="text-[var(--mt-red)]" />{contactInfo.email}</li>
                  <li className="flex items-center gap-3 text-white/65 text-sm"><Phone size={18} className="text-[var(--mt-red)]" />{contactInfo.phone}</li>
                  <li className="flex items-center gap-3 text-white/65 text-sm"><MapPin size={18} className="text-[var(--mt-red)]" />{contactInfo.location}</li>
                </ul>
                <div className="flex gap-4 mt-6">
                  {contactInfo.socials.map((s) => (
                    <a key={s.label} href={s.url} className="text-white/50 hover:text-[var(--mt-red)] transition-colors text-xs uppercase tracking-wider font-display">{s.label}</a>
                  ))}
                </div>

                <div className="mt-8 pt-6 border-t border-white/10">
                  <span className="font-display uppercase tracking-[0.25em] text-xs text-[var(--mt-red)]">El siguiente paso</span>
                  <ul className="mt-4 space-y-3">
                    {contactSteps.map((st) => (
                      <li key={st.n} className="flex gap-3">
                        <span className="mt-num text-sm">{st.n}</span>
                        <span className="text-white/60 text-sm">{st.text}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* CLOSING */}
      <section className="bg-[var(--mt-red)] py-20 md:py-24">
        <div className="max-w-[900px] mx-auto px-5 md:px-8 text-center">
          <Reveal>
            <h2 className="font-display font-bold uppercase text-3xl md:text-5xl text-white leading-tight">
              Tu espacio ya tiene una personalidad.
              <span className="block text-black/80">Vamos a encontrar su ritmo.</span>
            </h2>
            <p className="mt-6 text-white/90 font-display uppercase tracking-wider text-sm">
              MusicTeam. El ritmo que da vida a los espacios.
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
